/**
 * Card Info Slider - Initializes Swiper on card info slider elements.
 */
import Swiper from 'swiper';
import { EffectFade, Autoplay, A11y } from 'swiper/modules';

export function setupCardInfoSlider(): void {
    const sliders = document.querySelectorAll<HTMLElement>('[data-card-info-slider]');

    sliders.forEach((slider) => {
        const imageEl = slider.querySelector<HTMLElement>('[data-card-info-slider-images]');
        const contentEl = slider.querySelector<HTMLElement>('[data-card-info-slider-content]');
        const categoryItems = slider.querySelectorAll<HTMLElement>('[data-card-info-slider-category]');
        const playPauseButton = slider.querySelector<HTMLButtonElement>('[data-card-info-slider-toggle]');
        const playPauseText = slider.querySelector<HTMLElement>('[data-card-info-slider-toggle-text]');

        if (!imageEl || !contentEl) {
            return;
        }

        const contentSwiper = new Swiper(contentEl, {
            modules: [EffectFade, A11y],
            effect: 'fade',
            fadeEffect: { crossFade: true },
            allowTouchMove: false,
            speed: 500,
            a11y: { enabled: true },
        });

        const imageSwiper = new Swiper(imageEl, {
            modules: [EffectFade, Autoplay, A11y],
            effect: 'fade',
            fadeEffect: { crossFade: true },
            speed: 500,
            a11y: { enabled: true },
            autoplay: {
                delay: 8000,
                disableOnInteraction: false,
            },
        });

        const setActiveCategory = (index: number): void => {
            categoryItems.forEach((item, itemIndex) => {
                item.classList.toggle('is-active', itemIndex === index);
            });
        };

        const syncSlides = (index: number): void => {
            contentSwiper.slideTo(index);
            setActiveCategory(index);
        };

        const setPausedState = (isPaused: boolean): void => {
            const label = isPaused ? 'Play slideshow' : 'Pause slideshow';

            playPauseButton?.classList.toggle('is-paused', isPaused);
            playPauseButton?.setAttribute('aria-pressed', String(!isPaused));
            playPauseButton?.setAttribute('aria-label', label);

            if (playPauseText) {
                playPauseText.textContent = label;
            }
        };

        playPauseButton?.addEventListener('click', () => {
            const isPaused = playPauseButton.classList.contains('is-paused');

            if (isPaused) {
                imageSwiper.autoplay.start();
                setPausedState(false);
            } else {
                imageSwiper.autoplay.stop();
                setPausedState(true);
            }
        });

        imageSwiper.on('slideChange', () => {
            syncSlides(imageSwiper.activeIndex);
        });

        syncSlides(0);

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            imageSwiper.autoplay.stop();
            setPausedState(true);
        }
    });
}

// Auto-initialize when loaded as entry
setupCardInfoSlider();
